import floraData from '@/data/flora.json';
import type { FloraDefinition } from './FloraDefinition';
import { assertValidFloraDefinition } from './FloraValidation';

interface FloraCatalogFile {
  catalogVersion: string;
  schemaVersion: number;
  plants: unknown[];
}

/**
 * Loads, validates, and indexes the shipped flora catalog (spec §8.1, §9). Every record is
 * validated up-front; invalid records throw with field-specific errors rather than being silently
 * dropped (spec §20).
 */
export class FloraCatalog {
  private readonly byId = new Map<string, FloraDefinition>();
  readonly version: string;

  constructor(file: FloraCatalogFile) {
    this.version = file.catalogVersion;
    for (const record of file.plants) {
      assertValidFloraDefinition(record);
      if (this.byId.has(record.id)) {
        throw new Error(`Duplicate flora id in catalog: ${record.id}`);
      }
      this.byId.set(record.id, record);
    }
  }

  /** Number of plants in the catalog. */
  get size(): number {
    return this.byId.size;
  }

  /** All definitions in load order. */
  list(): FloraDefinition[] {
    return [...this.byId.values()];
  }

  /** Look up a definition by id, or throw when unknown. */
  get(id: string): FloraDefinition {
    const def = this.byId.get(id);
    if (!def) {
      throw new Error(`Unknown flora id: ${id}`);
    }
    return def;
  }

  /** Look up a definition by id, returning undefined when unknown. */
  find(id: string): FloraDefinition | undefined {
    return this.byId.get(id);
  }

  has(id: string): boolean {
    return this.byId.has(id);
  }
}

/** The default catalog loaded from the shipped `flora.json`. */
export function loadDefaultCatalog(): FloraCatalog {
  return new FloraCatalog(floraData);
}
